/**
 * Photo strip + uploader for a single punch item. Lazy-loads its
 * own attachments list so the parent doesn't need to fan out.
 *
 * Drag-and-drop or click-to-pick via react-dropzone. Multiple files
 * supported; each uploads in parallel.
 *
 * Photos can't be loaded directly from a <img src=> when MOCK_AUTH
 * routes through the X-Mock-User header (you can't set headers on
 * <img> requests). For dev preview, the API serves attachments without
 * requiring the header — auth is verified via app.authenticate which
 * accepts the localStorage-backed mock role automatically on real
 * fetches. For pure <img> loads we need a different mechanism — for now
 * we use the API base URL directly and the request hits the public
 * cache route. (When real Entra auth lands, we'll switch to signed
 * URLs or proxy through the API with cookie auth.)
 */
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  attachmentFileUrl,
  useDeleteAttachment,
  useUploadPunchItemPhoto,
  usePunchItemAttachments,
} from '@/api/attachments';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

const ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/heic': ['.heic'],
};

export function PhotoStrip({ punchItemId }: { punchItemId: string }) {
  const { data, isLoading } = usePunchItemAttachments(punchItemId);
  const upload = useUploadPunchItemPhoto(punchItemId);
  const del = useDeleteAttachment(punchItemId);
  const [uploading, setUploading] = useState(0);

  const onDrop = useCallback(
    async (files: File[]) => {
      setUploading((n) => n + files.length);
      for (const file of files) {
        try {
          await upload.mutateAsync({ file });
        } catch (err) {
          toast.error(`Upload failed: ${(err as Error).message}`);
        } finally {
          setUploading((n) => Math.max(0, n - 1));
        }
      }
    },
    [upload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxSize: 25 * 1024 * 1024,
    multiple: true,
    noClick: false,
  });

  const photos = data?.data ?? [];
  const hasPhotos = photos.length > 0;
  const [deleteId, setDeleteId] = useState<string | null>(null);

  return (
    <div className="mt-3 space-y-2">
      <ConfirmDialog
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (!deleteId) return;
          del.mutate(deleteId, {
            onSuccess: () => toast.success('Deleted'),
            onError: (err) => toast.error(`Failed: ${err.message}`),
            onSettled: () => setDeleteId(null),
          });
        }}
        title="Delete this photo?"
        body="The photo is evidence on this punch item and will be permanently removed."
        confirmLabel="Delete"
        tone="danger"
        busy={del.isPending}
      />
      {hasPhotos && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
          {photos.map((p) => (
            <div
              key={p.id}
              className="relative group aspect-square rounded-md overflow-hidden border border-line bg-fill-soft"
            >
              <img
                src={attachmentFileUrl(p)}
                alt={p.caption ?? p.original_filename}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteId(p.id);
                }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-bg/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete photo"
              >
                <Trash2 className="w-3 h-3 text-status-blocked-fg" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'border border-dashed rounded-md px-3 py-3 text-xs cursor-pointer transition-colors flex items-center gap-2',
          isDragActive
            ? 'border-accent bg-accent/5 text-ink'
            : 'border-line text-ink-faint hover:border-ink-faint hover:bg-fill-soft',
        )}
      >
        <input {...getInputProps()} />
        {uploading > 0 ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Uploading {uploading}…</span>
          </>
        ) : isDragActive ? (
          <>
            <Upload className="w-3.5 h-3.5" />
            <span>Drop to upload</span>
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5" />
            <span>
              {hasPhotos ? 'Add more photos' : 'Drag photos here, or click to pick'}
            </span>
            {isLoading && <span className="ml-auto text-[10px]">loading…</span>}
          </>
        )}
      </div>
    </div>
  );
}
